import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Users, Building2, Package, IndianRupee, LifeBuoy, ScanLine, BellRing, Tag, Download, X, Wallet as WalletIcon, TrendingUp, CheckCircle2, Circle, Settings, Lock, MapPin, Inbox, MessageCircle, Filter, Map, Trophy, Mail, Copy, Trash2, QrCode } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import IndiaHeatmap from '../components/IndiaHeatmap';
import { DispatchTagsDialog } from '../components/DispatchTagsDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboardPage() {
  const { token } = useAuth();
  const [tab, setTab] = useState('overview');
  const [overview, setOverview] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [partners, setPartners] = useState([]);
  const [orders, setOrders] = useState([]);
  const [dispatch, setDispatch] = useState(null);
  const [rsa, setRsa] = useState([]);
  const [creditFor, setCreditFor] = useState(null);
  const [partnerApps, setPartnerApps] = useState([]);
  const [waLogs, setWaLogs] = useState([]);
  const [sales, setSales] = useState(null);
  const [salesFilters, setSalesFilters] = useState({ group_by: 'city', city: '', district: '', state: '', start: '', end: '' });
  const [territories, setTerritories] = useState([]);
  const [tState, setTState] = useState('');
  const [tPartnerId, setTPartnerId] = useState('');
  const [tHover, setTHover] = useState(null);
  const [emails, setEmails] = useState([]);
  const [provisionedCreds, setProvisionedCreds] = useState(null); // {name, identifier, password, referral_code}

  // Filters for admin lists
  const [custFilters, setCustFilters] = useState({ order_start: '', order_end: '', tag_type: 'all' });
  const [partnerFilters, setPartnerFilters] = useState({ joined_start: '', joined_end: '', tag_type: 'all' });
  const [orderFilters, setOrderFilters] = useState({ order_start: '', order_end: '', tag_type: 'all', customer_type: 'all', payment_mode: 'all', source: 'all' });

  // RSA vendors
  const [rsaVendors, setRsaVendors] = useState([]);
  const [rsaAlerts, setRsaAlerts] = useState([]);
  const [vendorForm, setVendorForm] = useState({ vendor_name: '', owner_name: '', phone: '', email: '', city: '', state: '', coverage_area: '', license_number: '', verified: false, notes: '' });
  const [sourceInsights, setSourceInsights] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    try {
      const [ov, c, p, o, r] = await Promise.all([
        axios.get(`${API}/admin/overview`, { headers }),
        axios.get(`${API}/admin/customers?${new URLSearchParams(Object.entries(custFilters).filter(([, v]) => v && v !== 'all')).toString()}`, { headers }),
        axios.get(`${API}/admin/partners?${new URLSearchParams(Object.entries(partnerFilters).filter(([, v]) => v && v !== 'all')).toString()}`, { headers }),
        axios.get(`${API}/admin/orders?${new URLSearchParams(Object.entries(orderFilters).filter(([, v]) => v && v !== 'all')).toString()}`, { headers }),
        axios.get(`${API}/admin/rsa`, { headers }),
      ]);
      setOverview(ov.data); setCustomers(c.data); setPartners(p.data); setOrders(o.data); setRsa(r.data);
    } catch (e) { console.error(e); }
  };

  const loadRsaVendors = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/rsa-vendors`, { headers });
      setRsaVendors(data);
      const alertsRes = await axios.get(`${API}/admin/rsa-alerts`, { headers });
      setRsaAlerts(alertsRes.data);
    } catch (e) { console.error(e); }
  };

  const loadSourceInsights = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/sales/source-insights?weeks=8`, { headers });
      setSourceInsights(data);
    } catch (e) { console.error(e); }
  };

  const saveVendor = async () => {
    if (!vendorForm.vendor_name.trim() || !vendorForm.phone.trim()) { toast({ title: 'Vendor name and phone are required' }); return; }
    try {
      await axios.post(`${API}/admin/rsa-vendors`, vendorForm, { headers });
      toast({ title: 'Vendor onboarded' });
      setVendorForm({ vendor_name: '', owner_name: '', phone: '', email: '', city: '', state: '', coverage_area: '', license_number: '', verified: false, notes: '' });
      loadRsaVendors();
    } catch (e) { toast({ title: 'Failed', description: e.response?.data?.detail || 'Try again' }); }
  };

  const toggleVendorVerified = async (v) => {
    try { await axios.patch(`${API}/admin/rsa-vendors/${v.id}`, { verified: !v.verified }, { headers }); loadRsaVendors(); }
    catch { toast({ title: 'Failed' }); }
  };

  const deleteVendor = async (id) => {
    if (!window.confirm('Delete this vendor?')) return;
    try { await axios.delete(`${API}/admin/rsa-vendors/${id}`, { headers }); loadRsaVendors(); }
    catch { toast({ title: 'Failed' }); }
  };

  const loadPartnerApps = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/partner-applications`, { headers });
      setPartnerApps(data);
    } catch (e) { console.error(e); }
  };

  const loadWaLogs = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/wa-confirmations`, { headers });
      setWaLogs(data);
    } catch (e) { console.error(e); }
  };

  const loadSales = async (f = salesFilters) => {
    try {
      const params = new URLSearchParams(Object.entries(f).filter(([, v]) => v));
      const { data } = await axios.get(`${API}/admin/sales/analytics?${params.toString()}`, { headers });
      setSales(data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (tab === 'partner_inbox') loadPartnerApps();
    if (tab === 'wa_log') loadWaLogs();
    if (tab === 'sales') { loadSales(salesFilters); loadSourceInsights(); }
    if (tab === 'territories') { loadTerritories(); }
    if (tab === 'emails') loadEmails();
    if (tab === 'rsa_vendors') loadRsaVendors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const loadTerritories = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/territories`, { headers });
      setTerritories(data);
    } catch (e) { console.error(e); }
  };

  const loadEmails = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/emails`, { headers });
      setEmails(data);
    } catch (e) { console.error(e); }
  };

  const assignTerritory = async () => {
    if (!tState.trim() || !tPartnerId) { toast({ title: 'Pick a state and a partner' }); return; }
    try {
      await axios.post(`${API}/admin/territories`, { state: tState.trim(), partner_id: tPartnerId }, { headers });
      toast({ title: `${tState} assigned` });
      setTState(''); setTPartnerId('');
      loadTerritories();
    } catch { toast({ title: 'Failed to assign' }); }
  };

  const removeTerritory = async (state) => {
    if (!window.confirm(`Unassign ${state}?`)) return;
    try { await axios.delete(`${API}/admin/territories/${encodeURIComponent(state)}`, { headers }); loadTerritories(); }
    catch { toast({ title: 'Failed' }); }
  };

  const exportCsv = async (extraQs = '') => {
    try {
      const r = await axios.get(`${API}/admin/export/orders.csv${extraQs}`, { headers, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'orders.csv'; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 3000);
    } catch (e) { toast({ title: 'Export failed' }); }
  };

  const updateRsa = async (id, status) => {
    try { await axios.patch(`${API}/admin/rsa/${id}`, { status }, { headers }); toast({ title: `RSA marked ${status}` }); load(); }
    catch { toast({ title: 'Failed' }); }
  };

  const updatePartnerApp = async (id, status) => {
    try {
      const { data } = await axios.patch(`${API}/admin/partner-applications/${id}`, { status }, { headers });
      toast({ title: `Marked ${status}` });
      if (status === 'onboarded' && data.provisioned) {
        const app = partnerApps.find((a) => a.id === id);
        setProvisionedCreds({ ...data.provisioned, name: app?.name || 'Partner' });
      }
      loadPartnerApps();
    } catch { toast({ title: 'Failed' }); }
  };

  const maxGrowth = Math.max(...(overview?.growth_14d?.map((g) => g.orders) || [1]), 1);

  return (
    <Layout>
      <section className="py-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <p className="eyebrow">Admin console</p>
              <h1 className="h-display text-3xl md:text-4xl mt-1">Overview</h1>
            </div>
            <a href="/admin/qr" className="btn-primary text-sm" data-testid="admin-qr-link"><QrCode size={14} /> Emergency QR Tags</a>
            <button onClick={() => exportCsv()} className="btn-outline text-sm" data-testid="admin-export-csv-btn"><Download size={14} /> Export orders CSV</button>
          </div>

          <div className="mt-8 border-b flex gap-6 overflow-x-auto">
            {[
              ['overview', 'Overview'],
              ['sales', 'Sales Analytics'],
              ['customers', 'Customers'],
              ['partners', 'Partners'],
              ['partner_inbox', 'Partner Inbox'],
              ['territories', 'Territories'],
              ['orders', 'Orders'],
              ['wa_log', 'WhatsApp Log'],
              ['emails', 'Emails Log'],
              ['rsa', 'RSA'],
              ['rsa_vendors', 'RSA Vendors'],
              ['settings', 'Settings'],
            ].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`} className={`py-3 text-sm font-medium border-b-2 whitespace-nowrap ${tab === k ? 'border-navy text-navy' : 'border-transparent text-neutral-500 hover:text-navy'}`}>{l}</button>
            ))}
          </div>

          <div className="mt-6">
            {tab === 'overview' && overview && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <StatCard icon={Users} label="Customers" value={overview.customers} />
                  <StatCard icon={Building2} label="Partners" value={overview.partners} />
                  <StatCard icon={Package} label="Paid orders" value={overview.orders_paid} sub={`${overview.orders_total} total`} />
                  <StatCard icon={IndianRupee} label="Revenue" value={`₹${overview.revenue}`} />
                  <StatCard icon={Tag} label="Tags sold" value={overview.tags_sold} />
                  <StatCard icon={CheckCircle2} label="Tags active" value={overview.tags_activated} sub={`${overview.tags_inactive} pending`} />
                  <StatCard icon={LifeBuoy} label="Open RSA" value={overview.rsa_open} sub={`${overview.rsa_total} total`} />
                  <StatCard icon={ScanLine} label="Total scans" value={overview.scans} />
                  <StatCard icon={BellRing} label="Reminders sent" value={overview.reminders_sent} />
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                  <div className="card-soft p-6">
                    <div className="flex justify-between items-center mb-4"><h3 className="font-semibold">Orders — last 14 days</h3><TrendingUp size={16} className="text-navy" /></div>
                    <div className="flex gap-1 items-end h-32">
                      {overview.growth_14d.map((g, i) => (
                        <div key={i} className="flex-1 flex flex-col items-center gap-1">
                          <div className="w-full bg-navy rounded-t" style={{ height: `${(g.orders / maxGrowth) * 100}%`, minHeight: '4px' }} title={`${g.date}: ${g.orders}`} />
                          <p className="text-[8px] text-neutral-500">{g.date.slice(-2)}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="card-soft p-6">
                    <div className="flex justify-between items-center mb-4"><h3 className="font-semibold">Top partners</h3><span className="text-xs text-neutral-500">by revenue</span></div>
                    {overview.top_partners.length === 0 && <p className="text-sm text-neutral-500 py-6 text-center">No partner sales yet.</p>}
                    {overview.top_partners.map((p, i) => (
                      <div key={p.partner_id} className="flex justify-between py-2 border-b last:border-0">
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 rounded-full bg-navy/10 text-navy text-xs font-bold flex items-center justify-center">{i + 1}</span>
                          <div><p className="text-sm font-medium">{p.name}</p><p className="text-xs text-neutral-500">{p.city} · {p.orders} orders</p></div>
                        </div>
                        <p className="font-semibold text-navy text-sm">₹{p.revenue}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {tab === 'sales' && (
              <div className="space-y-5" data-testid="admin-sales-analytics">
                <div className="card-soft p-5">
                  <div className="flex items-center gap-2 mb-4"><Filter size={16} className="text-navy" /><h3 className="font-semibold">Filters</h3></div>
                  <div className="grid md:grid-cols-6 gap-3">
                    <div>
                      <label className="text-xs text-neutral-500">Group by</label>
                      <select
                        value={salesFilters.group_by}
                        onChange={(e) => setSalesFilters({ ...salesFilters, group_by: e.target.value })}
                        className="mt-1 w-full px-3 py-2 rounded-lg border border-black/15 bg-white text-sm"
                        data-testid="sales-groupby-select"
                      >
                        <option value="city">City</option>
                        <option value="district">District</option>
                        <option value="state">State</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-neutral-500">City</label>
                      <input list="cities-list" value={salesFilters.city} onChange={(e) => setSalesFilters({ ...salesFilters, city: e.target.value })} placeholder="Any" className="mt-1 w-full px-3 py-2 rounded-lg border border-black/15 text-sm" data-testid="sales-city-filter" />
                      <datalist id="cities-list">{(sales?.distinct?.city || []).map((v) => <option key={v} value={v} />)}</datalist>
                    </div>
                    <div>
                      <label className="text-xs text-neutral-500">District</label>
                      <input list="districts-list" value={salesFilters.district} onChange={(e) => setSalesFilters({ ...salesFilters, district: e.target.value })} placeholder="Any" className="mt-1 w-full px-3 py-2 rounded-lg border border-black/15 text-sm" data-testid="sales-district-filter" />
                      <datalist id="districts-list">{(sales?.distinct?.district || []).map((v) => <option key={v} value={v} />)}</datalist>
                    </div>
                    <div>
                      <label className="text-xs text-neutral-500">State</label>
                      <input list="states-list" value={salesFilters.state} onChange={(e) => setSalesFilters({ ...salesFilters, state: e.target.value })} placeholder="Any" className="mt-1 w-full px-3 py-2 rounded-lg border border-black/15 text-sm" data-testid="sales-state-filter" />
                      <datalist id="states-list">{(sales?.distinct?.state || []).map((v) => <option key={v} value={v} />)}</datalist>
                    </div>
                    <div>
                      <label className="text-xs text-neutral-500">From</label>
                      <input type="date" value={salesFilters.start} onChange={(e) => setSalesFilters({ ...salesFilters, start: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-black/15 text-sm" data-testid="sales-start-filter" />
                    </div>
                    <div>
                      <label className="text-xs text-neutral-500">To</label>
                      <input type="date" value={salesFilters.end} onChange={(e) => setSalesFilters({ ...salesFilters, end: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-black/15 text-sm" data-testid="sales-end-filter" />
                    </div>
                  </div>
                  <div className="mt-4 flex gap-2 flex-wrap">
                    <button onClick={() => loadSales(salesFilters)} className="btn-primary text-sm" data-testid="sales-apply-btn">Apply filters</button>
                    <button
                      onClick={() => { const empty = { group_by: 'city', city: '', district: '', state: '', start: '', end: '' }; setSalesFilters(empty); loadSales(empty); }}
                      className="btn-outline text-sm"
                      data-testid="sales-reset-btn"
                    >Reset</button>
                    <button
                      onClick={() => {
                        const q = new URLSearchParams(Object.entries({ city: salesFilters.city, district: salesFilters.district, state: salesFilters.state }).filter(([, v]) => v)).toString();
                        exportCsv(q ? `?${q}` : '');
                      }}
                      className="btn-outline text-sm"
                      data-testid="sales-export-btn"
                    ><Download size={14} /> Export filtered CSV</button>
                  </div>
                </div>

                {sales && (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      <StatCard icon={Package} label="Total orders" value={sales.totals.orders} />
                      <StatCard icon={CheckCircle2} label="Paid orders" value={sales.totals.orders_paid} />
                      <StatCard icon={IndianRupee} label="Revenue" value={`₹${sales.totals.revenue}`} />
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="card-soft p-5" data-testid="sales-chart-revenue-30d">
                        <div className="flex items-center gap-2 mb-1"><TrendingUp size={14} className="text-navy" /><h4 className="text-sm font-semibold">Revenue — last 30 days</h4></div>
                        <p className="text-xs text-neutral-500 mb-3">Paid orders only</p>
                        <RevenueLineChart series={sales.revenue_series || []} />
                      </div>
                      <div className="card-soft p-5" data-testid="sales-chart-top-cities">
                        <div className="flex items-center gap-2 mb-1"><MapPin size={14} className="text-navy" /><h4 className="text-sm font-semibold">Top 5 cities</h4></div>
                        <p className="text-xs text-neutral-500 mb-3">by revenue</p>
                        <TopCitiesBar rows={sales.top_cities || []} />
                      </div>
                    </div>

                    {sourceInsights && (
                      <div className="card-soft p-5" data-testid="sales-chart-source-insights">
                        <div className="flex items-center gap-2 mb-1"><Inbox size={14} className="text-navy" /><h4 className="text-sm font-semibold">Where paid orders come from</h4></div>
                        <p className="text-xs text-neutral-500 mb-4">Week-on-week acquisition source · last 8 weeks</p>
                        <SourceInsightsChart data={sourceInsights} />
                      </div>
                    )}

                    <div className="card-soft overflow-hidden">
                      <div className="flex items-center gap-2 p-4 border-b bg-cream">
                        <MapPin size={14} className="text-navy" />
                        <p className="text-sm font-semibold capitalize">Breakdown by {sales.group_by}</p>
                        <span className="text-xs text-neutral-500 ml-auto">{sales.rows.length} {sales.group_by === 'city' ? 'cities' : sales.group_by === 'district' ? 'districts' : 'states'}</span>
                      </div>
                      <table className="w-full text-sm">
                        <thead className="bg-cream/50">
                          <tr>
                            <th className="text-left py-3 px-4 capitalize">{sales.group_by}</th>
                            <th className="text-right py-3 px-4">Orders</th>
                            <th className="text-right py-3 px-4">Paid</th>
                            <th className="text-right py-3 px-4">Tags sold</th>
                            <th className="text-right py-3 px-4">Revenue</th>
                            <th className="text-right py-3 px-4">% Share</th>
                          </tr>
                        </thead>
                        <tbody data-testid="sales-breakdown-table">
                          {sales.rows.map((r) => {
                            const share = sales.totals.revenue > 0 ? (r.revenue / sales.totals.revenue) * 100 : 0;
                            return (
                              <tr key={r[sales.group_by]} className="border-t">
                                <td className="py-3 px-4 font-medium">{r[sales.group_by]}</td>
                                <td className="py-3 px-4 text-right">{r.orders}</td>
                                <td className="py-3 px-4 text-right">{r.orders_paid}</td>
                                <td className="py-3 px-4 text-right">{r.tags_sold}</td>
                                <td className="py-3 px-4 text-right font-semibold text-navy">₹{Math.round(r.revenue)}</td>
                                <td className="py-3 px-4 text-right">
                                  <div className="flex items-center gap-2 justify-end">
                                    <div className="w-16 h-1.5 bg-black/5 rounded-full overflow-hidden"><div className="h-full bg-navy" style={{ width: `${share}%` }} /></div>
                                    <span className="text-xs text-neutral-500 w-10 text-right">{share.toFixed(1)}%</span>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                          {sales.rows.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-neutral-500">No sales data for the selected filters.</td></tr>}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            )}

            {tab === 'partner_inbox' && (
              <div className="space-y-3" data-testid="admin-partner-inbox">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-neutral-500">Enquiries submitted from the "Become a Partner" contact form.</p>
                  </div>
                  <span className="text-xs text-neutral-500">{partnerApps.length} total · {partnerApps.filter((a) => a.status === 'new').length} new</span>
                </div>
                {partnerApps.length === 0 && <div className="card-soft p-10 text-center text-neutral-500">No partner enquiries yet.</div>}
                {partnerApps.map((a) => (
                  <div key={a.id} className="card-soft p-5 flex flex-col md:flex-row md:items-start md:justify-between gap-3" data-testid={`partner-app-${a.id}`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold">{a.name}</p>
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-navy/10 text-navy font-semibold uppercase">{a.type}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase ${a.status === 'contacted' ? 'bg-yellow-100 text-yellow-700' : a.status === 'onboarded' ? 'bg-green-100 text-green-700' : a.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>{a.status || 'new'}</span>
                      </div>
                      <p className="text-xs text-neutral-500 mt-1">{a.business_name || '—'} · {a.city}</p>
                      <div className="mt-2 text-sm text-neutral-700">
                        <a href={`mailto:${a.email}`} className="text-navy underline mr-3">{a.email}</a>
                        <a href={`tel:${a.phone}`} className="text-navy underline">{a.phone}</a>
                      </div>
                      {a.message && <p className="mt-2 text-sm text-neutral-600 italic">"{a.message}"</p>}
                      <p className="mt-2 text-xs text-neutral-500">Received {new Date(a.created_at).toLocaleString()}</p>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {a.status !== 'contacted' && <button onClick={() => updatePartnerApp(a.id, 'contacted')} className="text-xs px-3 py-1.5 border rounded-full hover:bg-cream" data-testid={`partner-app-contacted-${a.id}`}>Mark contacted</button>}
                      {a.status !== 'onboarded' && <button onClick={() => updatePartnerApp(a.id, 'onboarded')} className="text-xs px-3 py-1.5 bg-navy text-white rounded-full" data-testid={`partner-app-onboarded-${a.id}`}>Onboarded</button>}
                      {a.status !== 'rejected' && <button onClick={() => updatePartnerApp(a.id, 'rejected')} className="text-xs px-3 py-1.5 border border-red-200 text-red-600 rounded-full hover:bg-red-50" data-testid={`partner-app-rejected-${a.id}`}>Reject</button>}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {tab === 'wa_log' && (
              <div className="space-y-3" data-testid="admin-wa-log">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-neutral-500">Order-received WhatsApp/SMS confirmations. Falls back to MOCKED when the virtual number is not configured.</p>
                  <button onClick={loadWaLogs} className="text-xs text-navy underline" data-testid="wa-log-refresh">Refresh</button>
                </div>
                <div className="card-soft overflow-hidden">
                  <table className="w-full text-sm min-w-[720px]">
                    <thead className="bg-cream">
                      <tr>
                        <th className="text-left py-3 px-4">When</th>
                        <th className="text-left py-3 px-4">Order</th>
                        <th className="text-left py-3 px-4">Phone</th>
                        <th className="text-left py-3 px-4">Status</th>
                        <th className="text-left py-3 px-4">Provider</th>
                        <th className="text-left py-3 px-4">Message</th>
                      </tr>
                    </thead>
                    <tbody>
                      {waLogs.map((w) => (
                        <tr key={w.id} className="border-t align-top" data-testid={`wa-log-${w.id}`}>
                          <td className="py-3 px-4 text-xs text-neutral-500 whitespace-nowrap">{new Date(w.created_at).toLocaleString()}</td>
                          <td className="py-3 px-4 font-mono text-xs whitespace-nowrap">{w.order_number}</td>
                          <td className="py-3 px-4 text-xs whitespace-nowrap">{w.phone || '—'}</td>
                          <td className="py-3 px-4">
                            <span className={`text-[10px] px-2 py-1 rounded-full font-semibold uppercase ${w.status === 'sent' ? 'bg-green-100 text-green-700' : w.status === 'mocked' ? 'bg-yellow-100 text-yellow-700' : w.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-neutral-100 text-neutral-600'}`}>{w.status}</span>
                          </td>
                          <td className="py-3 px-4 text-xs">{w.provider}{w.provider_sid ? ` · ${w.provider_sid.slice(0, 10)}…` : ''}</td>
                          <td className="py-3 px-4 text-xs text-neutral-600 whitespace-pre-line max-w-md">{w.message}</td>
                        </tr>
                      ))}
                      {waLogs.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-neutral-500">No confirmations logged yet.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {tab === 'territories' && (
              <div className="space-y-4" data-testid="admin-territories">
                <div className="card-soft p-5">
                  <div className="flex items-center gap-2 mb-4"><Map size={16} className="text-navy" /><h3 className="font-semibold">Assign a state to a partner</h3></div>
                  <div className="grid md:grid-cols-3 gap-3">
                    <input value={tState} onChange={(e) => setTState(e.target.value)} placeholder="State name (e.g. Gujarat)" className="px-3 py-2 rounded-lg border border-black/15 text-sm" data-testid="territory-state-input" list="all-states" />
                    <datalist id="all-states">{['Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Delhi','Goa','Gujarat','Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala','Madhya Pradesh','Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland','Odisha','Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana','Tripura','Uttar Pradesh','Uttarakhand','West Bengal'].map((s) => <option key={s} value={s} />)}</datalist>
                    <select value={tPartnerId} onChange={(e) => setTPartnerId(e.target.value)} className="px-3 py-2 rounded-lg border border-black/15 bg-white text-sm" data-testid="territory-partner-select">
                      <option value="">Select partner…</option>
                      {partners.map((p) => (
                        <option key={p.id} value={p.id}>{p.business_name || p.name} · {p.city || '—'}</option>
                      ))}
                    </select>
                    <button onClick={assignTerritory} className="btn-primary text-sm justify-center" data-testid="territory-assign-btn">Assign</button>
                  </div>
                  <p className="text-xs text-neutral-500 mt-2">All future scan-leads whose vehicle-owner state matches will auto-route to this partner.</p>
                </div>

                <div className="grid lg:grid-cols-5 gap-4">
                  <div className="lg:col-span-3 card-soft p-5">
                    <div className="flex items-center gap-2 mb-3"><Map size={14} className="text-navy" /><h4 className="text-sm font-semibold">India coverage heatmap</h4></div>
                    <IndiaHeatmap assignments={territories} onHover={setTHover} />
                    <div className="mt-3 min-h-[42px] text-xs" data-testid="territory-heatmap-hover">
                      {tHover ? (
                        tHover.unassigned ? (
                          <p><span className="font-semibold">{tHover.state}</span> · <span className="text-neutral-500">Open — no partner assigned yet</span></p>
                        ) : (
                          <p><span className="font-semibold">{tHover.state}</span> · Assigned to <span className="font-semibold text-navy">{tHover.partner_name}</span>{tHover.partner_city ? ` (${tHover.partner_city})` : ''}</p>
                        )
                      ) : <p className="text-neutral-400">Hover a state to preview its assignment.</p>}
                    </div>
                  </div>

                  <div className="lg:col-span-2 card-soft overflow-hidden">
                    <div className="p-4 border-b bg-cream text-sm font-semibold">Active assignments ({territories.length})</div>
                    <div className="max-h-[520px] overflow-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-cream/50 sticky top-0"><tr><th className="text-left py-3 px-4">State</th><th className="text-left py-3 px-4">Partner</th><th className="text-right py-3 px-4"></th></tr></thead>
                        <tbody data-testid="territories-table">
                          {territories.map((t) => (
                            <tr key={t.state} className="border-t">
                              <td className="py-3 px-4 font-semibold">{t.state}</td>
                              <td className="py-3 px-4">
                                <p>{t.partner_name}</p>
                                <p className="text-xs text-neutral-500">{t.partner_city}</p>
                              </td>
                              <td className="py-3 px-4 text-right"><button onClick={() => removeTerritory(t.state)} className="text-xs text-red-600 hover:underline inline-flex items-center gap-1" data-testid={`territory-remove-${t.state}`}><Trash2 size={12} /></button></td>
                            </tr>
                          ))}
                          {territories.length === 0 && <tr><td colSpan={3} className="py-8 text-center text-neutral-500 text-xs">No assignments yet.</td></tr>}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {tab === 'emails' && (
              <div className="space-y-3" data-testid="admin-emails-log">
                <p className="text-sm text-neutral-500">Outbound emails from the platform. Currently MOCKED — wire Resend/SMTP for real delivery.</p>
                <div className="card-soft overflow-hidden">
                  <table className="w-full text-sm min-w-[720px]">
                    <thead className="bg-cream"><tr><th className="text-left py-3 px-4">When</th><th className="text-left py-3 px-4">To</th><th className="text-left py-3 px-4">Subject</th><th className="text-left py-3 px-4">Status</th><th className="text-left py-3 px-4">Body</th></tr></thead>
                    <tbody>
                      {emails.map((e) => (
                        <tr key={e.id} className="border-t align-top">
                          <td className="py-3 px-4 text-xs text-neutral-500 whitespace-nowrap">{new Date(e.created_at).toLocaleString()}</td>
                          <td className="py-3 px-4 text-xs">{e.to}</td>
                          <td className="py-3 px-4">{e.subject}</td>
                          <td className="py-3 px-4"><span className={`text-[10px] px-2 py-1 rounded-full font-semibold uppercase ${e.status === 'sent' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{e.status}</span></td>
                          <td className="py-3 px-4 text-xs text-neutral-600 whitespace-pre-line max-w-md">{e.body}</td>
                        </tr>
                      ))}
                      {emails.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-neutral-500">No emails logged yet.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {tab === 'customers' && (
              <div className="space-y-3" data-testid="admin-customers-tab">
                <div className="card-soft p-4 flex flex-wrap items-end gap-3">
                  <div>
                    <label className="text-[11px] text-neutral-500 uppercase">Order date from</label>
                    <input type="date" value={custFilters.order_start} onChange={(e) => setCustFilters({ ...custFilters, order_start: e.target.value })} className="mt-1 block text-sm px-3 py-1.5 rounded-lg border border-black/15" data-testid="cust-order-start" />
                  </div>
                  <div>
                    <label className="text-[11px] text-neutral-500 uppercase">Order date to</label>
                    <input type="date" value={custFilters.order_end} onChange={(e) => setCustFilters({ ...custFilters, order_end: e.target.value })} className="mt-1 block text-sm px-3 py-1.5 rounded-lg border border-black/15" data-testid="cust-order-end" />
                  </div>
                  <div>
                    <label className="text-[11px] text-neutral-500 uppercase">Tag type</label>
                    <select value={custFilters.tag_type} onChange={(e) => setCustFilters({ ...custFilters, tag_type: e.target.value })} className="mt-1 block text-sm px-3 py-1.5 rounded-lg border border-black/15 bg-white" data-testid="cust-tag-filter">
                      <option value="all">All</option>
                      <option value="bike">Bike</option>
                      <option value="car">Car</option>
                      <option value="combo">Combo</option>
                    </select>
                  </div>
                  <button onClick={load} className="btn-primary text-sm" data-testid="cust-apply-btn">Apply</button>
                  <button onClick={() => { setCustFilters({ order_start: '', order_end: '', tag_type: 'all' }); setTimeout(load, 50); }} className="btn-outline text-sm">Reset</button>
                </div>
                <div className="card-soft overflow-hidden">
                  <table className="w-full text-sm min-w-[860px]">
                    <thead className="bg-cream">
                      <tr>
                        <th className="text-left py-3 px-4">Name</th>
                        <th className="text-left py-3 px-4">Contact</th>
                        <th className="text-left py-3 px-4">Tag type</th>
                        <th className="text-left py-3 px-4">Customer type</th>
                        <th className="text-left py-3 px-4">Wallet</th>
                        <th className="text-left py-3 px-4">Joined</th>
                        <th className="py-3 px-4"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {customers.map((c) => (
                        <tr key={c.id} className="border-t">
                          <td className="py-3 px-4 font-medium">{c.name}</td>
                          <td className="py-3 px-4 text-neutral-600">{c.email}<br /><span className="text-xs text-neutral-500">{c.phone}</span></td>
                          <td className="py-3 px-4"><span className="text-[10px] px-2 py-1 rounded-full bg-navy/10 text-navy font-semibold uppercase">{c.tag_type || '—'}</span></td>
                          <td className="py-3 px-4"><span className={`text-[10px] px-2 py-1 rounded-full font-semibold uppercase ${c.customer_type === 'referral_dealer' ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}`}>{c.customer_type === 'referral_dealer' ? 'Referral / Dealer' : 'Direct'}</span></td>
                          <td className="py-3 px-4">₹{c.wallet_balance || 0}</td>
                          <td className="py-3 px-4 text-neutral-500 text-xs">{new Date(c.created_at).toLocaleDateString()}</td>
                          <td className="py-3 px-4 text-right"><button onClick={() => setCreditFor(c)} className="text-xs text-navy font-semibold hover:underline">Credit wallet</button></td>
                        </tr>
                      ))}
                      {customers.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-neutral-500">No customers match your filters</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {tab === 'partners' && (
              <div className="space-y-3" data-testid="admin-partners-tab">
                <div className="card-soft p-4 flex flex-wrap items-end gap-3">
                  <div>
                    <label className="text-[11px] text-neutral-500 uppercase">Joining date from</label>
                    <input type="date" value={partnerFilters.joined_start} onChange={(e) => setPartnerFilters({ ...partnerFilters, joined_start: e.target.value })} className="mt-1 block text-sm px-3 py-1.5 rounded-lg border border-black/15" data-testid="partner-joined-start" />
                  </div>
                  <div>
                    <label className="text-[11px] text-neutral-500 uppercase">Joining date to</label>
                    <input type="date" value={partnerFilters.joined_end} onChange={(e) => setPartnerFilters({ ...partnerFilters, joined_end: e.target.value })} className="mt-1 block text-sm px-3 py-1.5 rounded-lg border border-black/15" data-testid="partner-joined-end" />
                  </div>
                  <div>
                    <label className="text-[11px] text-neutral-500 uppercase">Tag type</label>
                    <select value={partnerFilters.tag_type} onChange={(e) => setPartnerFilters({ ...partnerFilters, tag_type: e.target.value })} className="mt-1 block text-sm px-3 py-1.5 rounded-lg border border-black/15 bg-white" data-testid="partner-tag-filter">
                      <option value="all">All</option>
                      <option value="bike">Bike</option>
                      <option value="car">Car</option>
                      <option value="combo">Combo</option>
                    </select>
                  </div>
                  <button onClick={load} className="btn-primary text-sm" data-testid="partner-apply-btn">Apply</button>
                  <button onClick={() => { setPartnerFilters({ joined_start: '', joined_end: '', tag_type: 'all' }); setTimeout(load, 50); }} className="btn-outline text-sm">Reset</button>
                </div>
                <div className="card-soft overflow-hidden">
                  <table className="w-full text-sm min-w-[860px]">
                    <thead className="bg-cream">
                      <tr>
                        <th className="text-left py-3 px-4">Business</th>
                        <th className="text-left py-3 px-4">Type</th>
                        <th className="text-left py-3 px-4">City</th>
                        <th className="text-left py-3 px-4">Tag type</th>
                        <th className="text-left py-3 px-4">Commission code</th>
                        <th className="text-left py-3 px-4">Commission earned</th>
                        <th className="text-left py-3 px-4">Joined</th>
                        <th className="text-left py-3 px-4">Contact</th>
                      </tr>
                    </thead>
                    <tbody>
                      {partners.map((p) => (
                        <tr key={p.id} className="border-t">
                          <td className="py-3 px-4 font-medium">{p.business_name || p.name}</td>
                          <td className="py-3 px-4 capitalize">{p.partner_type || '—'}</td>
                          <td className="py-3 px-4">{p.city || '—'}</td>
                          <td className="py-3 px-4"><span className="text-[10px] px-2 py-1 rounded-full bg-navy/10 text-navy font-semibold uppercase">{p.tag_type || '—'}</span></td>
                          <td className="py-3 px-4 font-mono text-xs">{p.referral_code || '—'}</td>
                          <td className="py-3 px-4 font-semibold text-navy">₹{p.commission_earned || 0}<br /><span className="text-[10px] text-neutral-500 font-normal">{p.commission_orders || 0} orders</span></td>
                          <td className="py-3 px-4 text-neutral-500 text-xs">{p.created_at ? new Date(p.created_at).toLocaleDateString() : '—'}</td>
                          <td className="py-3 px-4 text-xs text-neutral-500">{p.email}<br />{p.phone}</td>
                        </tr>
                      ))}
                      {partners.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-neutral-500">No partners match your filters</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {tab === 'orders' && (
              <div className="space-y-3" data-testid="admin-orders-tab">
                <div className="card-soft p-4 flex flex-wrap items-end gap-3">
                  <div>
                    <label className="text-[11px] text-neutral-500 uppercase">From</label>
                    <input type="date" value={orderFilters.order_start} onChange={(e) => setOrderFilters({ ...orderFilters, order_start: e.target.value })} className="mt-1 block text-sm px-3 py-1.5 rounded-lg border border-black/15" data-testid="order-start" />
                  </div>
                  <div>
                    <label className="text-[11px] text-neutral-500 uppercase">To</label>
                    <input type="date" value={orderFilters.order_end} onChange={(e) => setOrderFilters({ ...orderFilters, order_end: e.target.value })} className="mt-1 block text-sm px-3 py-1.5 rounded-lg border border-black/15" data-testid="order-end" />
                  </div>
                  <div>
                    <label className="text-[11px] text-neutral-500 uppercase">Tag type</label>
                    <select value={orderFilters.tag_type} onChange={(e) => setOrderFilters({ ...orderFilters, tag_type: e.target.value })} className="mt-1 block text-sm px-3 py-1.5 rounded-lg border border-black/15 bg-white" data-testid="order-tag-filter">
                      <option value="all">All</option><option value="bike">Bike</option><option value="car">Car</option><option value="combo">Combo</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[11px] text-neutral-500 uppercase">Customer</label>
                    <select value={orderFilters.customer_type} onChange={(e) => setOrderFilters({ ...orderFilters, customer_type: e.target.value })} className="mt-1 block text-sm px-3 py-1.5 rounded-lg border border-black/15 bg-white" data-testid="order-customer-filter">
                      <option value="all">All</option><option value="direct">Direct</option><option value="referral_dealer">Referral / Dealer</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[11px] text-neutral-500 uppercase">Payment</label>
                    <select value={orderFilters.payment_mode} onChange={(e) => setOrderFilters({ ...orderFilters, payment_mode: e.target.value })} className="mt-1 block text-sm px-3 py-1.5 rounded-lg border border-black/15 bg-white" data-testid="order-payment-filter">
                      <option value="all">All</option><option value="online">Online</option><option value="cod">COD</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[11px] text-neutral-500 uppercase">Source</label>
                    <select value={orderFilters.source} onChange={(e) => setOrderFilters({ ...orderFilters, source: e.target.value })} className="mt-1 block text-sm px-3 py-1.5 rounded-lg border border-black/15 bg-white" data-testid="order-source-filter">
                      <option value="all">All</option>
                      <option value="online">Online</option>
                      <option value="social_media">Social media</option>
                      <option value="friends_family">Friends / Family</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <button onClick={load} className="btn-primary text-sm" data-testid="order-apply-btn">Apply</button>
                  <button onClick={() => { setOrderFilters({ order_start: '', order_end: '', tag_type: 'all', customer_type: 'all', payment_mode: 'all', source: 'all' }); setTimeout(load, 50); }} className="btn-outline text-sm">Reset</button>
                </div>

                <div className="card-soft overflow-x-auto">
                  <table className="w-full text-sm min-w-[1250px]">
                    <thead className="bg-cream">
                      <tr>
                        <th className="text-left py-3 px-4">Order #</th>
                        <th className="text-left py-3 px-4">Date</th>
                        <th className="text-left py-3 px-4">Items</th>
                        <th className="text-left py-3 px-4">Tag type</th>
                        <th className="text-left py-3 px-4">Location</th>
                        <th className="text-left py-3 px-4">Payment mode</th>
                        <th className="text-left py-3 px-4">Customer type</th>
                        <th className="text-left py-3 px-4">Source</th>
                        <th className="text-left py-3 px-4">Total / Paid</th>
                        <th className="text-left py-3 px-4">Status</th>
                        <th className="text-left py-3 px-4">Shipment</th>
                        <th className="text-left py-3 px-4">QR Tags</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((o) => (
                        <tr key={o.id} className="border-t">
                          <td className="py-3 px-4 font-medium">{o.order_number}</td>
                          <td className="py-3 px-4 text-xs text-neutral-500">{new Date(o.created_at).toLocaleString()}</td>
                          <td className="py-3 px-4 text-xs">{(o.items || []).map((i) => `${i.name} x${i.qty}`).join(', ')}</td>
                          <td className="py-3 px-4"><span className="text-[10px] px-2 py-1 rounded-full bg-navy/10 text-navy font-semibold uppercase">{o.tag_type || '—'}</span></td>
                          <td className="py-3 px-4 text-xs">{o.location || '—'}</td>
                          <td className="py-3 px-4"><span className={`text-[10px] px-2 py-1 rounded-full font-semibold uppercase ${o.payment_mode === 'cod' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'}`}>{o.payment_mode || 'online'}</span></td>
                          <td className="py-3 px-4"><span className={`text-[10px] px-2 py-1 rounded-full font-semibold uppercase ${o.customer_type === 'referral_dealer' ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}`}>{o.customer_type === 'referral_dealer' ? 'Referral' : 'Direct'}</span></td>
                          <td className="py-3 px-4 text-xs capitalize">{(o.source || '—').replace('_', ' ')}</td>
                          <td className="py-3 px-4">₹{o.total}{o.wallet_used ? ` · -₹${o.wallet_used}` : ''}<br /><span className="text-xs text-neutral-500">{o.status === 'paid' ? 'Paid' : 'Payable'} ₹{o.payable != null ? o.payable : o.total}</span></td>
                          <td className="py-3 px-4"><span className={`text-[10px] px-2 py-1 rounded-full font-semibold uppercase ${o.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-navy/10 text-navy'}`}>{o.status}</span></td>
                          <td className="py-3 px-4">
                            {(o.status === 'paid' || o.payment_mode === 'cod') ? (
                              <select
                                value={o.shipment_status || 'placed'}
                                onChange={async (e) => {
                                  const st = e.target.value;
                                  if (['shipped', 'delivered'].includes(st) && (o.assigned_tag_ids?.length || 0) < (o.tags_needed || 0)) { setDispatch({ order: o, status: st }); return; }
                                  try {
                                    await axios.patch(`${API}/admin/orders/${o.id}/shipment`, { shipment_status: st }, { headers });
                                    toast({ title: `Shipment updated to ${st}` });
                                    load();
                                  } catch (ex) { toast({ title: ex.response?.data?.detail || 'Failed' }); }
                                }}
                                className="text-xs px-2 py-1 rounded border border-black/15 bg-white"
                                data-testid={`shipment-select-${o.order_number}`}
                              >
                                {['placed', 'packed', 'shipped', 'delivered', 'cancelled'].map((s) => <option key={s} value={s}>{s}</option>)}
                              </select>
                            ) : <span className="text-xs text-neutral-400">—</span>}
                          </td>
                          <td className="py-3 px-4">
                            <p className="text-[10px] text-neutral-500 mb-1" data-testid={`order-tag-count-${o.order_number}`}>{o.assigned_tag_ids?.length || 0}/{o.tags_needed} tag · {o.stickers} sticker</p>
                            <OrderTagsCell key={(o.assigned_tag_ids || []).join(',')} orderRef={o.order_number || o.id} headers={headers} />
                          </td>
                        </tr>
                      ))}
                      {orders.length === 0 && <tr><td colSpan={12} className="py-8 text-center text-neutral-500">No orders match your filters</td></tr>}
                    </tbody>
                  </table>
                  {dispatch && (
                    <DispatchTagsDialog
                      order={dispatch.order}
                      status={dispatch.status}
                      onClose={() => setDispatch(null)}
                      onSubmit={async (tagIds) => {
                        try {
                          await axios.patch(`${API}/admin/orders/${dispatch.order.id}/shipment`, { shipment_status: dispatch.status, tag_ids: tagIds }, { headers });
                          toast({ title: `Tags assigned · ${dispatch.status}` });
                          setDispatch(null); load();
                        } catch (ex) { toast({ title: ex.response?.data?.detail || 'Failed' }); }
                      }}
                    />
                  )}
                </div>
              </div>
            )}

            {tab === 'rsa_vendors' && (
              <div className="space-y-4" data-testid="admin-rsa-vendors">
                <div className="card-soft p-5">
                  <div className="flex items-center gap-2 mb-4"><LifeBuoy size={16} className="text-navy" /><h3 className="font-semibold">Onboard an RSA vendor</h3></div>
                  <div className="grid md:grid-cols-3 gap-3">
                    <input value={vendorForm.vendor_name} onChange={(e) => setVendorForm({ ...vendorForm, vendor_name: e.target.value })} placeholder="Vendor name*" className="px-3 py-2 rounded-lg border border-black/15 text-sm" data-testid="vendor-name-input" />
                    <input value={vendorForm.owner_name} onChange={(e) => setVendorForm({ ...vendorForm, owner_name: e.target.value })} placeholder="Owner name" className="px-3 py-2 rounded-lg border border-black/15 text-sm" data-testid="vendor-owner-input" />
                    <input value={vendorForm.phone} onChange={(e) => setVendorForm({ ...vendorForm, phone: e.target.value })} placeholder="Phone*" className="px-3 py-2 rounded-lg border border-black/15 text-sm" data-testid="vendor-phone-input" />
                    <input value={vendorForm.email} onChange={(e) => setVendorForm({ ...vendorForm, email: e.target.value })} placeholder="Email" className="px-3 py-2 rounded-lg border border-black/15 text-sm" data-testid="vendor-email-input" />
                    <input value={vendorForm.city} onChange={(e) => setVendorForm({ ...vendorForm, city: e.target.value })} placeholder="City" className="px-3 py-2 rounded-lg border border-black/15 text-sm" data-testid="vendor-city-input" />
                    <input value={vendorForm.state} onChange={(e) => setVendorForm({ ...vendorForm, state: e.target.value })} placeholder="State" className="px-3 py-2 rounded-lg border border-black/15 text-sm" data-testid="vendor-state-input" />
                    <input value={vendorForm.coverage_area} onChange={(e) => setVendorForm({ ...vendorForm, coverage_area: e.target.value })} placeholder="Coverage area (e.g. 25km radius)" className="px-3 py-2 rounded-lg border border-black/15 text-sm md:col-span-2" data-testid="vendor-coverage-input" />
                    <input value={vendorForm.license_number} onChange={(e) => setVendorForm({ ...vendorForm, license_number: e.target.value })} placeholder="License #" className="px-3 py-2 rounded-lg border border-black/15 text-sm" data-testid="vendor-license-input" />
                  </div>
                  <div className="mt-3 flex items-center gap-3 flex-wrap">
                    <label className="inline-flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={vendorForm.verified} onChange={(e) => setVendorForm({ ...vendorForm, verified: e.target.checked })} data-testid="vendor-verified-toggle" />
                      Mark as <span className="font-semibold text-green-700">Verified</span> on onboarding
                    </label>
                    <button onClick={saveVendor} className="btn-primary text-sm ml-auto" data-testid="vendor-save-btn">Onboard vendor</button>
                  </div>
                </div>

                <div className="card-soft overflow-hidden">
                  <div className="p-4 border-b bg-cream text-sm font-semibold flex items-center justify-between">
                    <span>Onboarded vendors ({rsaVendors.length})</span>
                    <span className="text-xs text-neutral-500 font-normal">{rsaVendors.filter((v) => v.verified).length} verified · {rsaVendors.filter((v) => !v.verified).length} unverified</span>
                  </div>
                  <table className="w-full text-sm min-w-[860px]">
                    <thead className="bg-cream/50"><tr><th className="text-left py-3 px-4">Vendor</th><th className="text-left py-3 px-4">Owner</th><th className="text-left py-3 px-4">Contact</th><th className="text-left py-3 px-4">City / State</th><th className="text-left py-3 px-4">Coverage</th><th className="text-left py-3 px-4">Verified</th><th className="text-right py-3 px-4"></th></tr></thead>
                    <tbody data-testid="rsa-vendors-table">
                      {rsaVendors.map((v) => (
                        <tr key={v.id} className="border-t">
                          <td className="py-3 px-4 font-medium">{v.vendor_name}<br /><span className="text-[10px] text-neutral-500 font-normal">Lic {v.license_number || '—'}</span></td>
                          <td className="py-3 px-4 text-neutral-600">{v.owner_name || '—'}</td>
                          <td className="py-3 px-4 text-xs">{v.phone}<br /><span className="text-neutral-500">{v.email || '—'}</span></td>
                          <td className="py-3 px-4 text-xs">{v.city || '—'}<br /><span className="text-neutral-500">{v.state || '—'}</span></td>
                          <td className="py-3 px-4 text-xs">{v.coverage_area || '—'}</td>
                          <td className="py-3 px-4">
                            <button onClick={() => toggleVendorVerified(v)} className={`inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full font-semibold uppercase ${v.verified ? 'bg-green-100 text-green-700' : 'bg-neutral-100 text-neutral-500'}`} data-testid={`vendor-verified-badge-${v.id}`}>
                              <CheckCircle2 size={11} /> {v.verified ? 'Verified' : 'Unverified'}
                            </button>
                          </td>
                          <td className="py-3 px-4 text-right">
                            <button onClick={() => deleteVendor(v.id)} className="text-xs text-red-600 hover:underline" data-testid={`vendor-delete-${v.id}`} aria-label="Delete vendor"><Trash2 size={12} /></button>
                          </td>
                        </tr>
                      ))}
                      {rsaVendors.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-neutral-500">No vendors onboarded yet.</td></tr>}
                    </tbody>
                  </table>
                </div>

                <div className="card-soft overflow-hidden">
                  <div className="p-4 border-b bg-cream text-sm font-semibold flex items-center justify-between">
                    <span className="flex items-center gap-2"><BellRing size={14} className="text-navy" /> Auto-routed RSA alerts ({rsaAlerts.length})</span>
                    <span className="text-xs text-neutral-500 font-normal">Verified vendors get notified when a scan happens in their state</span>
                  </div>
                  <table className="w-full text-sm min-w-[720px]">
                    <thead className="bg-cream/50"><tr><th className="text-left py-3 px-4">When</th><th className="text-left py-3 px-4">Vendor</th><th className="text-left py-3 px-4">State / City</th><th className="text-left py-3 px-4">Tag</th><th className="text-left py-3 px-4">Trigger</th><th className="text-left py-3 px-4">Status</th></tr></thead>
                    <tbody data-testid="rsa-alerts-table">
                      {rsaAlerts.map((a) => (
                        <tr key={a.id} className="border-t">
                          <td className="py-3 px-4 text-xs text-neutral-500 whitespace-nowrap">{new Date(a.created_at).toLocaleString()}</td>
                          <td className="py-3 px-4">{a.vendor_name}<br /><span className="text-[10px] text-neutral-500">{a.vendor_phone}</span></td>
                          <td className="py-3 px-4 text-xs">{a.state}{a.city ? ` · ${a.city}` : ''}</td>
                          <td className="py-3 px-4 font-mono text-xs">{a.tag_id}</td>
                          <td className="py-3 px-4 text-xs uppercase">{a.triggered_by}</td>
                          <td className="py-3 px-4"><span className={`text-[10px] px-2 py-1 rounded-full font-semibold uppercase ${a.status === 'sms_sent' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{a.status}</span></td>
                        </tr>
                      ))}
                      {rsaAlerts.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-neutral-500 text-xs">No auto-routed alerts yet. Onboard verified vendors and a scan in their state will fire an alert.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {tab === 'settings' && (
              <div className="max-w-lg">
                <div className="card-soft p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-11 h-11 rounded-xl bg-navy text-white flex items-center justify-center"><Lock size={18} /></div>
                    <div>
                      <h3 className="font-semibold">Change admin password</h3>
                      <p className="text-xs text-neutral-500">Use a strong password — at least 8 characters.</p>
                    </div>
                  </div>
                  <ChangePasswordForm headers={headers} />
                </div>

                <div className="card-soft p-6 mt-4">
                  <h3 className="font-semibold flex items-center gap-2"><BellRing size={16} /> Maintenance jobs</h3>
                  <p className="text-xs text-neutral-500 mt-1">Manually run scheduled jobs (usually run daily automatically).</p>
                  <div className="mt-4 flex gap-2 flex-wrap">
                    <button
                      onClick={async () => { try { const r = await axios.post(`${API}/admin/reminders/run`, {}, { headers }); toast({ title: 'Reminder job ran', description: `${r.data.total_reminders_sent} reminders sent so far` }); } catch { toast({ title: 'Failed' }); } }}
                      className="btn-outline text-sm"
                    >Run reminder job</button>
                    <button
                      onClick={async () => { try { await axios.post(`${API}/admin/wallet/expiry/run`, {}, { headers }); toast({ title: 'Wallet expiry job ran' }); load(); } catch { toast({ title: 'Failed' }); } }}
                      className="btn-outline text-sm"
                    >Run wallet expiry</button>
                  </div>
                </div>
              </div>
            )}

            {tab === 'rsa' && (
              <div className="space-y-3">
                {rsa.map((r) => (
                  <div key={r.id} className="card-soft p-5 flex justify-between items-start gap-4">
                    <div>
                      <p className="font-semibold">{r.issue_type}</p>
                      <p className="text-xs text-neutral-500 mt-1">{r.location} · {new Date(r.created_at).toLocaleString()}</p>
                      <p className="text-xs text-neutral-600 mt-2">{r.notes}</p>
                    </div>
                    <div className="text-right">
                      <span className={`text-[10px] px-2 py-1 rounded-full font-semibold uppercase ${r.status === 'resolved' ? 'bg-green-100 text-green-700' : r.status === 'in_progress' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'}`}>{r.status}</span>
                      <div className="mt-3 flex gap-2">
                        {r.status !== 'in_progress' && <button onClick={() => updateRsa(r.id, 'in_progress')} className="text-xs px-3 py-1.5 border rounded-full hover:bg-cream">In progress</button>}
                        {r.status !== 'resolved' && <button onClick={() => updateRsa(r.id, 'resolved')} className="text-xs px-3 py-1.5 bg-navy text-white rounded-full">Resolve</button>}
                      </div>
                    </div>
                  </div>
                ))}
                {rsa.length === 0 && <div className="card-soft p-10 text-center text-neutral-500">No RSA requests yet</div>}
              </div>
            )}
          </div>
        </div>
      </section>

      {creditFor && <CreditModal target={creditFor} onClose={() => setCreditFor(null)} onSaved={load} headers={headers} />}
      {provisionedCreds && <ProvisionedCredsModal creds={provisionedCreds} onClose={() => setProvisionedCreds(null)} />}
    </Layout>
  );
}

function RevenueLineChart({ series }) {
  const w = 480, h = 140, pad = 20;
  const max = Math.max(...series.map((s) => s.revenue), 1);
  const step = (w - pad * 2) / Math.max(series.length - 1, 1);
  const points = series.map((s, i) => `${pad + i * step},${h - pad - (s.revenue / max) * (h - pad * 2)}`).join(' ');
  const areaPath = `M${pad},${h - pad} L${points.split(' ').join(' L')} L${w - pad},${h - pad} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-40" preserveAspectRatio="none">
      <defs>
        <linearGradient id="revGrad" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#0B2E6F" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#0B2E6F" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill="url(#revGrad)" />
      <polyline points={points} fill="none" stroke="#0B2E6F" strokeWidth="2" strokeLinejoin="round" />
      {series.map((s, i) => (
        <circle key={s.date} cx={pad + i * step} cy={h - pad - (s.revenue / max) * (h - pad * 2)} r={s.revenue > 0 ? 2 : 0} fill="#0B2E6F">
          <title>{s.date}: ₹{s.revenue}</title>
        </circle>
      ))}
      <text x={pad} y={h - 4} className="fill-neutral-400" fontSize="9">{series[0]?.date.slice(5)}</text>
      <text x={w - pad} y={h - 4} textAnchor="end" className="fill-neutral-400" fontSize="9">{series[series.length - 1]?.date.slice(5)}</text>
      <text x={w - pad} y={pad - 4} textAnchor="end" className="fill-neutral-400" fontSize="9">peak ₹{Math.round(max)}</text>
    </svg>
  );
}

function TopCitiesBar({ rows }) {
  const max = Math.max(...rows.map((r) => r.revenue), 1);
  if (rows.length === 0) return <p className="text-sm text-neutral-500 py-6 text-center">No paid orders yet.</p>;
  return (
    <div className="space-y-2.5">
      {rows.map((r) => (
        <div key={r.city}>
          <div className="flex justify-between text-xs mb-1"><span className="font-medium">{r.city}</span><span className="text-neutral-500">₹{Math.round(r.revenue)}</span></div>
          <div className="h-2 bg-black/5 rounded-full overflow-hidden"><div className="h-full rounded-full" style={{ width: `${(r.revenue / max) * 100}%`, background: 'linear-gradient(90deg, #0B2E6F, #F59E0B)' }} /></div>
        </div>
      ))}
    </div>
  );
}

function OrderTagsCell({ orderRef, headers }) {
  const [tags, setTags] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showPick, setShowPick] = useState(false);
  const [avail, setAvail] = useState([]);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/qr/order/${encodeURIComponent(orderRef)}/tags`, { headers });
      setTags(r.data.tags || []);
      setLoaded(true);
    } catch {
      setLoaded(true);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [orderRef]);

  const openPicker = async () => {
    try {
      const r = await axios.get(`${API}/admin/qr/tags?status=available`, { headers });
      setAvail(r.data.slice(0, 30));
      setShowPick(true);
    } catch { toast({ title: 'Failed to fetch tags' }); }
  };
  const alloc = async (tag_id) => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/qr/allocate`, { tag_id, order_id: orderRef }, { headers });
      toast({ title: `Allocated ${tag_id}` });
      setShowPick(false);
      await load();
    } catch (e) { toast({ title: 'Failed', description: e.response?.data?.detail || '' }); } finally { setBusy(false); }
  };

  if (!loaded) return <span className="text-xs text-neutral-400">…</span>;
  return (
    <div className="text-xs" data-testid={`order-tags-cell-${orderRef}`}>
      {tags.length > 0 ? (
        <div className="space-y-1">
          {tags.map((t) => (
            <div key={t.tag_id} className="flex items-center gap-1.5">
              <span className="font-mono font-semibold">{t.tag_id}</span>
              <span className={`text-[9px] px-1.5 py-0.5 rounded-full uppercase ${t.status === 'active' ? 'bg-green-100 text-green-700' : t.status === 'allocated' ? 'bg-yellow-100 text-yellow-700' : 'bg-neutral-100 text-neutral-600'}`}>{t.status}</span>
            </div>
          ))}
        </div>
      ) : <span className="text-neutral-400">—</span>}
      <button onClick={openPicker} className="text-navy hover:underline mt-1 text-[11px] font-semibold" data-testid={`allocate-more-${orderRef}`}>+ Allocate tag</button>
      {showPick && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setShowPick(false)}>
          <div className="bg-white rounded-2xl p-5 max-w-md w-full max-h-[70vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold mb-3">Allocate a tag to order {orderRef}</h3>
            {avail.length === 0 && <p className="text-sm text-neutral-500">No available tags — generate more from the QR module.</p>}
            <div className="space-y-1">
              {avail.map((t) => (
                <button key={t.tag_id} disabled={busy} onClick={() => alloc(t.tag_id)} className="w-full text-left text-sm px-3 py-2 rounded-lg border border-black/10 hover:bg-navy hover:text-white hover:border-navy transition-colors flex items-center justify-between disabled:opacity-60">
                  <span className="font-mono">{t.tag_id}</span>
                  <span className="text-[10px] opacity-70">{t.permanent_url}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SourceInsightsChart({ data }) {
  const COLORS = { online: '#0B2E6F', social_media: '#F59E0B', friends_family: '#10B981', partners: '#8B5CF6', other: '#94A3B8' };
  const LABELS = { online: 'Online', social_media: 'Social', friends_family: 'Friends / Family', partners: 'Partners', other: 'Other' };
  const weeks = data.weeks || [];
  const grand = data.grand_total || 0;
  const stackedTotals = weeks.map((_, i) => data.sources.reduce((sum, s) => sum + (data.series[s]?.[i] || 0), 0));
  const scaleMax = Math.max(...stackedTotals, 1);

  return (
    <div className="w-full">
      <div className="flex items-end gap-2 h-40" data-testid="source-insights-chart">
        {weeks.map((w, i) => {
          const wTotal = stackedTotals[i];
          return (
            <div key={w} className="flex-1 flex flex-col items-center justify-end h-full group cursor-default">
              <div className="w-full flex flex-col-reverse justify-start rounded-t-sm overflow-hidden" style={{ height: `${(wTotal / scaleMax) * 100}%`, minHeight: wTotal > 0 ? 3 : 0 }} title={`Week of ${w}: ${wTotal} orders`}>
                {data.sources.map((s) => {
                  const c = data.series[s]?.[i] || 0;
                  if (c === 0) return null;
                  const pct = (c / wTotal) * 100;
                  return <div key={s} style={{ height: `${pct}%`, background: COLORS[s] }} className="w-full" title={`${LABELS[s]}: ${c}`} />;
                })}
              </div>
              <span className="mt-1.5 text-[9px] text-neutral-500 font-mono">{w.slice(5)}</span>
            </div>
          );
        })}
      </div>
      <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2 text-xs">
        {data.sources.map((s) => (
          <div key={s} className="inline-flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm" style={{ background: COLORS[s] }} />
            <span className="font-medium">{LABELS[s]}</span>
            <span className="text-neutral-500">{data.totals[s]}{grand ? ` · ${Math.round((data.totals[s] / grand) * 100)}%` : ''}</span>
          </div>
        ))}
      </div>
    </div>
  );
}


function ProvisionedCredsModal({ creds, onClose }) {
  const copy = (v) => { try { navigator.clipboard.writeText(v); toast({ title: 'Copied' }); } catch {} };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="provisioned-creds-modal">
      <div className="card-soft w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-2">
          <div className="flex items-center gap-2"><CheckCircle2 size={18} className="text-green-600" /><h3 className="font-semibold">Partner provisioned</h3></div>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-black/5"><X size={18} /></button>
        </div>
        <p className="text-xs text-neutral-500 mb-4">Share these credentials with <span className="font-semibold text-neutral-800">{creds.name}</span>. A copy has been logged in Emails tab (currently MOCKED).</p>
        {creds.reused_existing ? (
          <div className="text-sm bg-yellow-50 border border-yellow-200 rounded-lg p-3">A user with this email/phone already existed and has been promoted to <b>partner</b>. Ask them to log in with their existing password.</div>
        ) : (
          <div className="space-y-2 text-sm">
            <Row label="Login URL" value={`${window.location.origin}/partner/login`} onCopy={copy} />
            <Row label="Identifier" value={creds.identifier} onCopy={copy} />
            <Row label="Temp password" value={creds.password} onCopy={copy} mono />
            <Row label="Referral code" value={creds.referral_code} onCopy={copy} mono />
          </div>
        )}
        <button onClick={onClose} className="btn-primary w-full justify-center mt-5">Done</button>
      </div>
    </div>
  );
}

function Row({ label, value, onCopy, mono }) {
  return (
    <div className="flex items-center gap-2 border rounded-lg px-3 py-2">
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase text-neutral-500">{label}</p>
        <p className={`${mono ? 'font-mono' : ''} text-sm truncate`}>{value}</p>
      </div>
      <button onClick={() => onCopy(value)} className="text-navy hover:bg-navy/5 p-1.5 rounded" title="Copy"><Copy size={13} /></button>
    </div>
  );
}


function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="card-soft p-4">
      <div className="flex items-center gap-2 text-neutral-500 text-xs"><Icon size={14} /> {label}</div>
      <p className="h-display text-2xl mt-2">{value}</p>
      {sub && <p className="text-[10px] text-neutral-500 mt-1">{sub}</p>}
    </div>
  );
}

function CreditModal({ target, onClose, onSaved, headers }) {
  const [amount, setAmount] = useState(100);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (amount <= 0) { toast({ title: 'Amount must be positive' }); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/wallet/credit`, { user_id: target.id, amount: Number(amount), note }, { headers });
      toast({ title: 'Wallet credited', description: `${target.name} · new balance ₹${data.new_balance}` });
      onSaved(); onClose();
    } catch (e) { toast({ title: 'Failed', description: e.response?.data?.detail || 'Try again' }); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={onClose}>
      <div className="card-soft w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4"><h3 className="font-semibold">Credit {target.name}'s wallet</h3><button onClick={onClose} className="p-1 rounded-full hover:bg-black/5"><X size={18} /></button></div>
        <div className="space-y-3">
          <div><label className="text-xs">Amount (₹)</label><input type="number" value={amount} onChange={(e) => setAmount(Number(e.target.value))} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-black/15" /></div>
          <div><label className="text-xs">Note</label><input value={note} onChange={(e) => setNote(e.target.value)} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-black/15" placeholder="Reason for credit" /></div>
        </div>
        <button disabled={busy} onClick={save} className="btn-primary w-full justify-center mt-5 disabled:opacity-60">{busy ? 'Crediting…' : `Credit ₹${amount}`}</button>
      </div>
    </div>
  );
}

function ChangePasswordForm({ headers }) {
  const [f, setF] = useState({ current_password: '', new_password: '', confirm: '' });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (f.new_password.length < 8) { toast({ title: 'Min 8 characters' }); return; }
    if (f.new_password !== f.confirm) { toast({ title: 'Passwords do not match' }); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/change-password`, { current_password: f.current_password, new_password: f.new_password }, { headers });
      toast({ title: 'Password updated' });
      setF({ current_password: '', new_password: '', confirm: '' });
    } catch (e) {
      toast({ title: 'Failed', description: e.response?.data?.detail || 'Try again' });
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <div><label className="text-xs">Current password</label><input type="password" value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} required className="mt-1 w-full px-3 py-2.5 rounded-lg border border-black/15 focus:ring-2 focus:ring-navy/30 focus:outline-none" /></div>
      <div><label className="text-xs">New password (min 8)</label><input type="password" value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} required className="mt-1 w-full px-3 py-2.5 rounded-lg border border-black/15 focus:ring-2 focus:ring-navy/30 focus:outline-none" /></div>
      <div><label className="text-xs">Confirm new password</label><input type="password" value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} required className="mt-1 w-full px-3 py-2.5 rounded-lg border border-black/15 focus:ring-2 focus:ring-navy/30 focus:outline-none" /></div>
      <button disabled={busy} type="submit" className="btn-primary w-full justify-center disabled:opacity-60">{busy ? 'Updating…' : 'Update password'}</button>
    </form>
  );
}
